import { logoutClientSession } from "@/utils/auth-session";
import { parseErrorMessage } from "@/utils/errors";

type JsonObject = Record<string, unknown>;

const rejectWithOptionalStructuredError = (message: string, body: JsonObject | null) => {
  const errorCode = body && typeof body.error_code === "string" ? body.error_code : undefined;
  const provider = body && typeof body.provider === "string" ? body.provider : undefined;
  const workflowNodeId =
    body &&
    "workflow_node_id" in body &&
    (typeof body.workflow_node_id === "string" || typeof body.workflow_node_id === "number")
      ? body.workflow_node_id
      : undefined;
  if (errorCode) {
    return Promise.reject({
      message,
      error_code: errorCode,
      provider,
      workflow_node_id: workflowNodeId,
    });
  }
  return Promise.reject(message);
};

export const handleResponse = async (response: Response) => {
  const isJson = response.headers.get("content-type")?.includes("application/json");

  // Clone the response before reading it to avoid 'body stream already read' error
  const clonedResponse = isJson ? await response.clone().json() : null;

  if (clonedResponse && clonedResponse.success === false && "data" in clonedResponse) {
    const { data } = clonedResponse;
    if (typeof data === "object" && data !== null && "error_code" in data) {
      const msg =
        "message" in data && typeof data.message === "string" ? data.message : "Unknown error";
      return rejectWithOptionalStructuredError(msg, data as JsonObject);
    }
    return Promise.reject(clonedResponse.data);
  }

  if (!response.ok && clonedResponse && !("status" in clonedResponse)) {
    const getErrorMessage = (): string => {
      if (!clonedResponse) {
        return "Unknown error";
      }

      if (typeof clonedResponse.message === "string") {
        return clonedResponse.message;
      }

      return clonedResponse.error || parseErrorMessage(clonedResponse.message);
    };

    const errorMessage = getErrorMessage();

    if (response.status === 401 && errorMessage === "Invalid token") {
      logoutClientSession({ redirectToLogin: true, notifyServer: true });
      return Promise.reject();
    }

    const errorCode =
      (clonedResponse && typeof clonedResponse.code === "string"
        ? clonedResponse.code
        : undefined) ??
      (clonedResponse && typeof clonedResponse.error === "string"
        ? clonedResponse.error
        : undefined);
    const redirectTo =
      clonedResponse && typeof clonedResponse.redirect_to === "string"
        ? clonedResponse.redirect_to
        : undefined;
    if (errorCode || redirectTo) {
      return Promise.reject({
        message: errorMessage,
        status: response.status,
        code: errorCode,
        redirect_to: redirectTo,
        body: clonedResponse,
      });
    }

    if (response.status === 409) {
      return Promise.reject({
        message: errorMessage,
        status: response.status,
        body: clonedResponse,
      });
    }

    return rejectWithOptionalStructuredError(errorMessage, clonedResponse as JsonObject);
  }

  return response.json();
};
